<script setup>
import { formApi } from '@/configs/forms-api';
import { ref, computed } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from '#app';
import { createApplicationSchema } from '@/configs/validation-schemas';
import { useRequest } from '@/composables/useRequest';
import { loadRecaptchaScript } from '@/composables/useRecaptcha';
import { useGlobalStore } from '@/stores/global';
import { useThanksData } from '@/composables/useThanksData';

const { t } = useI18n();
const thanksData = useThanksData();
const router = useRouter();
const route = useRoute();
const globalStore = useGlobalStore();


//const emit = defineEmits(['success', 'fail']);

const loading = ref(false);
const captchaSiteKey = computed(() => globalStore.captchaSiteKey);

const formType = computed(() => globalStore?.applicationPopup?.type || '');
const services = computed(() => globalStore?.applicationPopup?.services || []);

const isDealerRequired = computed(() => formType.value === 'service' && services.value.length > 0);
const applicationSchema = computed(() => createApplicationSchema(t, isDealerRequired.value));

const currentId = computed(() => {
  const popup = globalStore.applicationPopup;
  const type = formType.value;

  if (['test_drive', 'consultation'].includes(type)) {
    return popup?.carId || null;
  }

  if (type === 'service' && !isDealerRequired.value) {
    return popup?.serviceId || null;
  }

  if (['autoservice', 'autocenter_application'].includes(type)) {
    return popup?.centerId || null;
  }

  return null;
});

const { handleSubmit, resetForm, setErrors } = useForm({
  validationSchema: applicationSchema,
  initialValues: {
    name: '',
    phone: '',
    question: '',
    dealer: null,
    page: '',
    id: currentId.value,
  },
});

const name = useField('name');
const phone = useField('phone');
const question = useField('question');
const dealer = useField('dealer');

const phoneMaskRegExp = /^\+380 \d{2} \d{3} \d{4}$/;
const isRequiredFilled = computed(() => {
  if (!phoneMaskRegExp.test(phone.value.value || '')) return false;

  return applicationSchema.value.isValidSync({
    name: name.value.value,
    phone: phone.value.value,
    question: question.value.value,
    dealer: dealer.value.value,
  });
});

const getCaptchaToken = async () => {
  const loaded = await loadRecaptchaScript(captchaSiteKey.value);

  return new Promise((resolve) => {
    if (!loaded || !window.grecaptcha || !captchaSiteKey.value) {
      console.warn('ReCaptcha script not loaded or site key missing');
      resolve(null);
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(captchaSiteKey.value, {
          action: formType.value || 'submit_form',
        })
        .then((token) => resolve(token))
        .catch((err) => {
          console.error('ReCaptcha execution error:', err);
          resolve(null);
        });
    });
  });
};

const onSubmit = handleSubmit(async (values) => {
  loading.value = true;
  const currentFullUrl = process.client ? window.location.href : '';
  try {
    const token = await getCaptchaToken();
    const targetId = isDealerRequired.value
      ? values.dealer
      : currentId.value;
    const formData = {
      name: values.name,
      question: values.question,
      phone: values.phone,
      page: currentFullUrl,
      id: targetId ? String(targetId) : null,
      token: token ? token : {},
    };
    if (formType.value === 'maintenance_calculator') {
      const maintenance = globalStore.applicationPopup?.maintenanceData;

      if (maintenance) {
        Object.assign(formData, {
          brand: maintenance.brand,
          model: maintenance.model,
          engine: maintenance.engine,
          maintenanceType: maintenance.maintenanceType,
          maintenance: maintenance.maintenance,
          mileage: maintenance.mileage,
        });
      }
    }
    const res = await useRequest(`/forms/${formApi[formType.value]}`, {
      method: 'POST',
      body: formData,
    });

    if (res && res.success !== false) {
      if (process.client) {
        window.dataLayer = window.dataLayer || [];
        const translatedFormName = formType?.value ? t(
          `application_popup.${formType.value}_title`,
        ) : '';
        window.dataLayer.push({
          event: 'form_submit',
          form_name: translatedFormName,
          form_id: formType.value,
          form_location: currentFullUrl,
          form_status: 'success',
        });
      }

      resetForm();
      globalStore.closeApplicationPopup();
      thanksData.value = res;
      const langPrefix = route.params.lang ? `/${route.params.lang}` : '';
      router.push(`${langPrefix}/thanks`);
    } else if (res && res.errors) {
      setErrors(res.errors);
      console.warn(res.header || 'Validation Error');
    }
  } catch (e) {
    if (e.data && e.data.errors) {
      setErrors(e.data.errors);
    }
    console.error('Submission error:', e);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <form
    class="form-application"
    :class="{ 'form-application--with-dealers': formType === 'service' && services.length > 0 }"
    @submit.prevent="onSubmit"
  >
    <div
      class="form-application__item form-application__item--name"
      :class="{ 'form-application__item--error': !!name.errorMessage.value }"
    >
      <FloatLabel variant="on" class="floatlabel-onwhite">
        <InputText
          id="applicationName"
          v-model="name.value.value"
          class="form-application__item-field"
          :invalid="!!name.errorMessage.value"
          type="text"
        />
        <label
          for="applicationName"
          class="form-application__item-label"
          :class="{ 'error-100': !!name.errorMessage.value }"
        >
          {{ $t('application_popup.first_name') }}*
        </label>
      </FloatLabel>
      <transition name="fade">
        <small
          v-if="name.errorMessage.value"
          class="form-application__error-text"
        >
          {{ name.errorMessage.value }}
        </small>
      </transition>
    </div>
    <div
      class="form-application__item form-application__item--phone"
      :class="{ 'form-application__item--error': !!phone.errorMessage.value }"
    >
      <FloatLabel variant="on" class="floatlabel-onwhite">
        <InputMask
          id="applicationPhone"
          v-model="phone.value.value"
          class="form-application__item-field"
          mask="+380 99 999 9999"
          placeholder="+380"
          fluid
          :invalid="!!phone.errorMessage.value"
        />
        <label
          for="applicationPhone"
          class="form-application__item-label"
          :class="{ 'error-100': !!phone.errorMessage.value }"
        >
          {{ $t('application_popup.phone') }}*
        </label>
      </FloatLabel>
      <transition name="fade">
        <small
          v-if="phone.errorMessage.value"
          class="form-application__error-text"
        >
          {{ phone.errorMessage.value }}
        </small>
      </transition>
    </div>
    <div
      v-if="isDealerRequired"
      class="form-application__item form-application__item--dealers"
      :class="{ 'form-application__item--error': !!dealer.errorMessage.value }"
    >
      <FloatLabel variant="on" class="floatlabel-onwhite">
        <Select
          v-model="dealer.value.value"
          :options="services"
          option-label="name"
          option-value="id"
          :placeholder="$t('application_popup.select_dealer')"
          input-id="dealer"
          :invalid="!!dealer.errorMessage.value"
          :empty-message="' '"
          class="select-onwhite select-with-borders form-application__item-select w-full"
          :appendTo="'self'"
          :pt="{
            overlay: { style: { width: '100%', minWidth: '100%' } }
          }"
        />
        <label for="dealer">{{
          $t('application_popup.dealer_label')
        }}*</label>
      </FloatLabel>
      <transition name="fade">
        <small
          v-if="dealer.errorMessage.value"
          class="form-application__error-text"
        >
          {{ dealer.errorMessage.value }}
        </small>
      </transition>
  </div>
    <div
      class="form-application__item form-application__item--question"
      :class="{
        'form-application__item--error': !!question.errorMessage.value,
      }"
    >
      <FloatLabel variant="on" class="floatlabel-onwhite">
        <Textarea
          id="applicationQuestion"
          v-model="question.value.value"
          class="form-application__item-textarea"
          :invalid="!!question.errorMessage.value"
          rows="5"
          cols="30"
          auto-resize
        />
        <label
          for="applicationQuestion"
          class="form-application__item-label"
          :class="{ 'error-100': !!question.errorMessage.value }"
        >
          {{ $t('application_popup.question') }}
        </label>
      </FloatLabel>
      <transition name="fade">
        <small
          v-if="question.errorMessage.value"
          class="form-application__error-text"
        >
          {{ question.errorMessage.value }}
        </small>
      </transition>
    </div>
    <div class="form-application__bottom">
      <p
        v-if="globalStore.termsLink || globalStore.policyLink"
        class="mb-0 form-application__text label"
      >
        {{ $t('application_popup.agreement_start') }}
        {{ $t('application_popup.send_button')
        }}{{ $t('application.agreement_middle') }}
        <CTagDetect
          v-if="globalStore.termsLink"
          :to="globalStore.termsLink"
          class="text-decoration-underline"
        >
          {{ $t('application_popup.agreement_terms_of_use') }}
        </CTagDetect>
        <template v-else>
          {{ $t('application_popup.agreement_terms_of_use') }}
        </template>
        {{ $t('application_popup.agreement_between') }}
        <CTagDetect
          v-if="globalStore.policyLink"
          :to="globalStore.policyLink"
          class="text-decoration-underline"
        >
          {{ $t('application_popup.agreement_privacy_policy') }}
        </CTagDetect>
        <template v-else>
          {{ $t('application_popup.agreement_privacy_policy') }}
        </template>
      </p>
      <Button
        type="submit"
        class="form-application__button link-normal button-black button-next-arrow js-button-application"
        :loading="loading"
        :disabled="!isRequiredFilled"
      >
        {{ $t('application_popup.send_button') }}
        <CIcon
          name="arrow-white"
          width="16"
          height="16"
          class="button-next-arrow__icon"
        />
      </Button>
    </div>
  </form>
</template>

<style scoped lang="scss">
.form-application {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr;
  grid-template-areas: 'name' 'phone' 'question' 'bottom';
  @include media-breakpoint-up(sm) {
    grid-template-columns: repeat(2, 6fr);
    grid-template-areas: 'name phone' 'question question' 'bottom bottom';
  }
  &--with-dealers {
    grid-template-areas: 'name' 'phone' 'dealers' 'question' 'bottom';
    @include media-breakpoint-up(sm) {
      grid-template-areas: 'name phone' 'dealers dealers' 'question question' 'bottom bottom';
    }
  }
  &__error {
    &-text {
      @include label;
      color: $error;
    }
  }
  &__item {
    &--name {
      grid-area: name;
    }
    &--phone {
      grid-area: phone;
    }
    &--question {
      grid-area: question;
    }
    &--dealers {
      grid-area: dealers;
    }
    &-label {
      @include body-3-medium;
      color: $on-surface-variant-2 !important;
    }
    &-field {
      @include body-3-medium;
      color: $on-surface-variant-2 !important;
      padding: 13px 16px 13px 16px;
      width: 100%;
    }
    &-select {
      width: 100%;
    }
    &-textarea {
      @include body-3-medium;
      color: $on-surface-variant-2 !important;
      padding: 13px 16px 13px 16px;
      width: 100%;
    }
    &--error {
      .form-application__item-label {
        color: $error !important;
      }
    }
  }
  &__bottom {
    align-items: start;
    display: grid;
    gap: 32px;
    grid-area: bottom;
    grid-template-columns: 1fr;
    @include media-breakpoint-up(sm) {
      gap: 48px;
      grid-template-columns: auto auto;
    }
  }
  &__button {
    width: fit-content;
  }
}

</style>
